import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import RocketLoader from './RocketLoader';

export const ResumeImportModal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState('idle');   // idle | parsing | error
  const [filename, setFilename] = useState('');
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);
  const didRun = useRef(false);
  const retryInputRef = useRef(null);

  const clearPendingImport = () => {
    localStorage.removeItem('mars_pending_file');
    localStorage.removeItem('mars_pending_filename');
  };

  const triggerImport = () => {
    const pending = localStorage.getItem('mars_pending_file');
    if (!pending || didRun.current) return;
    const pendingFilename = localStorage.getItem('mars_pending_filename') || 'resume.pdf';
    didRun.current = true;
    setState('idle');
    setError('');
    setFilename(pendingFilename);
    setVisible(true);
    parseAndCreate(pending, pendingFilename);
  };

  useEffect(() => {
    if (!user) return;
    triggerImport();
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) { didRun.current = false; triggerImport(); } };
    window.addEventListener('mars_trigger_import', handler);
    return () => window.removeEventListener('mars_trigger_import', handler);
  }, [user]);

  // Normalise parsed resume data to match the builder's schema.
  // Guards against old prompt versions or edge-case AI output.
  const normalise = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const d = { ...raw };

    // experience: position → title, flatten HTML bullets
    if (Array.isArray(d.experience)) {
      d.experience = d.experience.map((e, i) => {
        const entry = { ...e, id: e.id || `exp-${i + 1}` };
        if (!entry.title && entry.position) { entry.title = entry.position; delete entry.position; }
        // Convert HTML <ul><li>…</li></ul> description to bullets array
        if (entry.description && !Array.isArray(entry.bullets)) {
          const matches = [...(entry.description.matchAll(/<li[^>]*>(.*?)<\/li>/gi))];
          if (matches.length) {
            entry.bullets = matches.map(m => m[1].replace(/<[^>]+>/g, '').trim());
            entry.description = '';
          }
        }
        if (!Array.isArray(entry.bullets)) entry.bullets = [];
        return entry;
      });
    }

    // projects: name → title, description → summary, url → link
    if (Array.isArray(d.projects)) {
      d.projects = d.projects.map((p, i) => {
        const entry = { ...p, id: p.id || `proj-${i + 1}` };
        if (!entry.title && entry.name) { entry.title = entry.name; delete entry.name; }
        if (!entry.summary && entry.description) { entry.summary = entry.description; delete entry.description; }
        if (!entry.link && entry.url) { entry.link = entry.url; delete entry.url; }
        if (!Array.isArray(entry.bullets)) entry.bullets = [];
        return entry;
      });
    }

    // education: strip unknown fields
    if (Array.isArray(d.education)) {
      d.education = d.education.map((e, i) => ({
        id: e.id || `edu-${i + 1}`,
        school: e.school || '',
        degree: [e.degree, e.field].filter(Boolean).join(' — ') || '',
        location: e.location || '',
        startDate: e.startDate || '',
        endDate: e.endDate || '',
        gpa: e.gpa || '',
      }));
    }

    // certifications: keep only known fields
    if (Array.isArray(d.certifications)) {
      d.certifications = d.certifications.map((c, i) => ({
        id: c.id || `cert-${i + 1}`,
        name: c.name || '',
        issuer: c.issuer || '',
        date: c.date || '',
      }));
    }

    // Remove fields the builder doesn't use
    delete d.awards; delete d.volunteering; delete d.references;
    delete d.referencesAvailableOnRequest;

    return d;
  };

  const parseAndCreate = async (base64, uploadName = 'resume.pdf') => {
    setState('parsing');
    try {
      // Convert base64 data URL back to Blob
      const fetchRes = await fetch(base64);
      const blob = await fetchRes.blob();
      const formData = new FormData();
      formData.append('resume', blob, uploadName || 'resume.pdf');

      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('rb_token');
      const res = await fetch(`${apiBase}/resume-parse`, {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Parsing failed');

      // Normalise field names then create document with the correct nested structure
      const resumeData = normalise(data.resumeData);
      const doc = await api.createDoc({
        type: 'resume',
        template: 'harvard_standard',
        title: resumeData?.personalInfo?.fullName || 'Imported Resume',
        data: resumeData,
      });
      clearPendingImport();
      setVisible(false);
      navigate(`/builder/resume/${doc._id}`);
    } catch (err) {
      setState('error');
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  const handleRetryFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setState('error');
      setError('Only PDF files are supported.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setState('error');
      setError('File must be under 5 MB.');
      return;
    }

    setVisible(true);
    setState('parsing');
    setError('');
    setFilename(file.name);
    didRun.current = true;

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      localStorage.setItem('mars_pending_file', base64);
      localStorage.setItem('mars_pending_filename', file.name);
      parseAndCreate(base64, file.name);
    } catch {
      setState('error');
      setError('Failed to read file. Please try again.');
    }
  };

  const handleSkip = () => {
    clearPendingImport();
    didRun.current = false;
    setVisible(false);
  };

  const handleRetry = () => {
    clearPendingImport();
    didRun.current = false;
    setError('');
    retryInputRef.current?.click();
  };

  if (!visible) return null;

  return (
    <>
      <input
        ref={retryInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleRetryFileChange}
      />
      {/* Backdrop */}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        {/* Card */}
        <div style={{
          background: 'white', borderRadius: 20, width: '100%', maxWidth: 440,
          boxShadow: '0 32px 80px rgba(0,0,0,0.25)', overflow: 'hidden',
          fontFamily: 'Inter, system-ui, sans-serif',
          animation: 'fadeUp 0.3s ease both',
        }}>
          <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>

          {/* Header bar */}
          <div style={{ background: 'linear-gradient(135deg,#EF1D28,#C5161D)', padding: '20px 28px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Importing Your Resume</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</div>
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '32px 28px 28px' }}>

            {/* ── Parsing state ── */}
            {state === 'parsing' && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0 16px' }}>
                <RocketLoader size={180} message="AI is reading your resume…" />
                <div style={{ marginTop: 12, fontSize: 13, color: '#64748B', textAlign: 'center', lineHeight: 1.6 }}>
                  Extracting your experience, skills, and education.<br />This takes about 15–30 seconds.
                </div>
              </div>
            )}

            {/* ── Error state ── */}
            {state === 'error' && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FFF1F2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 32 }}>
                  ❌
                </div>
                <div style={{ fontSize: 19, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>Import failed</div>
                <div style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, marginBottom: 12 }}>
                  We couldn't import your resume automatically.
                </div>
                <div style={{ background: '#FFF1F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#DC2626', marginBottom: 24, textAlign: 'left' }}>
                  {error}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleSkip}
                    style={{ flex: 1, background: 'none', border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: '#64748B' }}>
                    Skip &amp; start fresh
                  </button>
                  <button onClick={handleRetry}
                    style={{ flex: 1, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Try another file
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};
